import React, { useState, useEffect, useRef } from "react";
import { DatePreset } from "../types";
import { DATE_PRESET_OPTIONS } from "../constants";
import { getDateRangeFromPreset } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
interface DateFilterProps {
  value: {
    start: Date | null;
    end: Date | null;
    preset: DatePreset | "";
  };
  onChange: (value: {
    start: Date | null;
    end: Date | null;
    preset: DatePreset | "";
  }) => void;
}

const DateFilter: React.FC<DateFilterProps> = ({ value, onChange }) => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [isOpen, setIsOpen] = useState(false);
  const [localPreset, setLocalPreset] = useState<DatePreset | "">(value.preset);
  const [localStartDate, setLocalStartDate] = useState<string>(
    value.start ? value.start.toISOString().split("T")[0] : ""
  );
  const [localEndDate, setLocalEndDate] = useState<string>(
    value.end ? value.end.toISOString().split("T")[0] : ""
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (localPreset && localPreset !== "custom") {
      const { start, end } = getDateRangeFromPreset(localPreset);
      setLocalStartDate(start.toISOString().split("T")[0]);
      setLocalEndDate(end.toISOString().split("T")[0]);
      onChange({ start, end, preset: localPreset });
    }
  }, [localPreset, onChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePresetSelect = (preset: DatePreset) => {
    setLocalPreset(preset);
    if (preset !== "custom") {
      const { start, end } = getDateRangeFromPreset(preset);
      setLocalStartDate(start.toISOString().split("T")[0]);
      setLocalEndDate(end.toISOString().split("T")[0]);
      onChange({ start, end, preset });
    } else {
      setLocalStartDate("");
      setLocalEndDate("");
      onChange({ start: null, end: null, preset: "custom" });
    }
    setIsOpen(false);
  };

  const handleCustomDateChange = () => {
    if (localStartDate && localEndDate) {
      const start = new Date(localStartDate);
      const end = new Date(localEndDate);
      onChange({ start, end, preset: "custom" });
    }
  };

  const handleClear = () => {
    setLocalPreset("");
    setLocalStartDate("");
    setLocalEndDate("");
    onChange({ start: null, end: null, preset: "" });
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (value.preset && value.preset !== "custom") {
      const option = DATE_PRESET_OPTIONS.find((opt) => opt.value === value.preset);
      return option?.label || Language?.groupAuditLogs?.filterByDate;
    }
    if (value.start && value.end) {
      return `${value.start.toLocaleDateString()} - ${value.end.toLocaleDateString()}`;
    }
    return Language?.groupAuditLogs?.filterByDate;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {Language?.groupAuditLogs?.date}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex justify-between items-center"
        >
          <span className={value.start && value.end ? "text-gray-900" : "text-gray-500"}>
            {getDisplayText()}
          </span>
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 min-w-80">
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-2">
              {DATE_PRESET_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePresetSelect(option.value)}
                  className={`px-3 py-2 text-sm rounded border ${
                    localPreset === option.value
                      ? "bg-blue-50 border-blue-500 text-blue-700"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {Language?.groupAuditLogs?.customRange}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {Language?.groupAuditLogs?.startDate}
                </label>
                <input
                  type="date"
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  {Language?.groupAuditLogs?.endDate}
                </label>
                <input
                  type="date"
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClear}
              className="text-sm text-red-600 hover:text-red-800"
            >
              {Language?.groupAuditLogs?.clear}
            </button>
          </div>
        </div>
      )}

      {value.start && value.end && (
        <div className="mt-2 flex items-center">
          <span className="text-sm text-gray-600">
            {value.start.toLocaleDateString()} - {value.end.toLocaleDateString()}
          </span>
          <button
            onClick={handleClear}
            className="ml-2 text-sm text-red-600 hover:text-red-800"
          >
            {Language?.groupAuditLogs?.clear}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateFilter;
